import { pickBy } from 'lodash';
import { createSelector } from 'reselect';

import { GlobalStateType } from 'Lib/reduxType';

import {
  OFFER_TYPE_DISCOUNT,
  OFFER_TYPE_INTRO_PRICE,
  OFFER_TYPE_SUB_OFFER,
} from 'Store/entities/constants';
import type { Product, ProductType } from 'Store/entities/products.types';
import { OFFER_ELIGIBLE_PRODUCTS } from 'Store/insendio/utils';

import { OFFERING_ENTITY_KEY, PURCHASES_ENTITY_KEY } from './entityKeys';
import {
  PRODUCT_TYPE_BOOST,
  PRODUCT_TYPE_BOOST_SUBSCRIPTION,
  PRODUCT_TYPE_BOUNCER_BYPASS,
  PRODUCT_TYPE_GOLD,
  PRODUCT_TYPE_PLATINUM,
  PRODUCT_TYPE_PLUS,
  PRODUCT_TYPE_PRIMETIME_BOOST,
  PRODUCT_TYPE_READ_RECEIPT,
  PRODUCT_TYPE_SELECT_SUBSCRIPTION,
  PRODUCT_TYPE_SUPERBOOST,
  PRODUCT_TYPE_SUPERLIKE,
  PRODUCT_TYPE_SWIPE_NOTE,
  PRODUCT_TYPES_ALL_SUBSCRIPTION,
  PRODUCT_TYPES_TIERED_SUBSCRIPTION,
} from './products.schema';
import {
  getAllProductsByType,
  getDiscountedProductsByType,
  getNonDiscountedProductsByType,
  getProductsForProductAndOfferType,
  getWeeklyProductsByType,
  removeReplaceableProducts,
  removeReplacementProducts,
} from './products.utils';
import { createEntitiesSelector } from './selectors';
import type { ProductsEntityState } from './types';

/**
 * Selects products from the root state based on lever value
 *
 * @param  {GlobalStateType} state - previous state of entities
 * @param  {String} lever - lever name to get state
 * @return {ProductsEntityState} offering entities
 */
export const selectProducts = createEntitiesSelector(OFFERING_ENTITY_KEY);

/**
 * Selects purchase products from the root state
 *
 * @param  {GlobalStateType} state - previous state of entities
 * @param  {String} lever - lever name to get state
 * @return {ProductsEntityState} purchase entities
 */
export const selectPurchaseProducts = createEntitiesSelector(PURCHASES_ENTITY_KEY);

/**
 * Selects discounted Tinder Gold intro-price products from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {ProductsEntityState} product entities with a product type of gold
 */
export const getIntroPriceProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    pickBy(products, (product): boolean => product.offerType === OFFER_TYPE_INTRO_PRICE)
);

/**
 * Selects the sub offer product from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Product} sub offer product
 */
export const getSubOfferProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    pickBy(products, (product): boolean => product.offerType === OFFER_TYPE_SUB_OFFER)
);

/**
 * Selects the product list based on type
 */
export const getProductListFromType = (
  productType: string,
  plusProducts: Product[],
  goldProducts: Product[],
  platinumProducts: Product[]
): Product[] => {
  switch (productType) {
    case PRODUCT_TYPE_GOLD:
      return goldProducts;
    case PRODUCT_TYPE_PLATINUM:
      return platinumProducts;
    case PRODUCT_TYPE_PLUS:
    default:
      return plusProducts;
  }
};

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns Tinder Plus products i.e. product entities with a product type of plus.
 */
export const getPlusProducts = createSelector(selectProducts, (products): ProductsEntityState => {
  const nonDiscountedProducts = getNonDiscountedProductsByType(products, PRODUCT_TYPE_PLUS);
  return removeReplacementProducts(nonDiscountedProducts);
});

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns Tinder Plus products that are replacement products.
 */
export const getReplacementPlusProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState => {
    const nonDiscountedProducts = getNonDiscountedProductsByType(products, PRODUCT_TYPE_PLUS);
    return removeReplaceableProducts(nonDiscountedProducts);
  }
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns Tinder Gold products i.e. product entities with a product type of gold.
 */
export const getGoldProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState => getNonDiscountedProductsByType(products, PRODUCT_TYPE_GOLD)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns Tinder Platinum products i.e. product entities with a product type of platinum.
 */
export const getPlatinumProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState => getNonDiscountedProductsByType(products, PRODUCT_TYPE_PLATINUM)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @returns Tinder Boost products i.e. product entities with a product type of boost.
 */
export const getBoostProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState => getNonDiscountedProductsByType(products, PRODUCT_TYPE_BOOST)
);

/**
 *  Selects Tinder SuperBoost products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getSuperBoostProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_SUPERBOOST)
);

/**
 *  Selects Tinder Primetime products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getPrimetimeBoostProducts = createSelector(
  selectProducts,
  (products: ProductsEntityState): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_PRIMETIME_BOOST)
);

/**
 *  Selects Tinder BoostSubscription products from the root state.
 */
export const getBoostSubscriptionProducts = createSelector(
  selectProducts,
  (products: ProductsEntityState): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_BOOST_SUBSCRIPTION)
);

/**
 * Selects Tinder Superlike products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getSuperLikeProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_SUPERLIKE)
);

/**
 *  Selects Read receipt products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getReadReceiptProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_READ_RECEIPT)
);

/**
 *  Selects Tinder Select subscription products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getTinderSelectProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_SELECT_SUBSCRIPTION)
);

/**
 *  Selects Swipe note products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getSwipeNoteProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_SWIPE_NOTE)
);

/**
 * Selects tiered subscription products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getTieredSubscriptionProducts = createSelector(
  selectPurchaseProducts,
  (products): ProductsEntityState =>
    getAllProductsByType(products, PRODUCT_TYPES_TIERED_SUBSCRIPTION)
);

/**
 * Selects non-discounted tiered subscription products from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getNonDiscountedTieredSubscriptionProducts = createSelector(
  selectProducts,
  (products: ProductsEntityState): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPES_TIERED_SUBSCRIPTION)
);

/**
 * Selects all subscription products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getAllSubscriptionProducts = createSelector(
  selectPurchaseProducts,
  (products): ProductsEntityState => getAllProductsByType(products, PRODUCT_TYPES_ALL_SUBSCRIPTION)
);

/**
 * Selects all products eligible for offer modal from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getOfferEligibleProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState => getAllProductsByType(products, OFFER_ELIGIBLE_PRODUCTS)
);

/**
 * Selects all discounted, offer modal eligible products from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const selectDiscountedProducts = createSelector(
  selectProducts,
  (products): ProductsEntityState =>
    getProductsForProductAndOfferType(products, OFFER_ELIGIBLE_PRODUCTS, OFFER_TYPE_DISCOUNT)
);

/**
 * Selects Bouncer Bypass products from the root state.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entities with a product type of bouncer bypass
 */
export const getBouncerBypassProducts = createSelector(
  selectProducts,
  (products: ProductsEntityState): ProductsEntityState =>
    getNonDiscountedProductsByType(products, PRODUCT_TYPE_BOUNCER_BYPASS)
);

/**
 * Selects products by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType | undefined} productType
 */
export const getNonDiscountedProductsFromProductType = createSelector(
  [selectProducts, (_state: GlobalStateType, productType: ProductType | undefined) => productType],
  (products: ProductsEntityState, productType): ProductsEntityState =>
    getNonDiscountedProductsByType(products, productType)
);

/**
 * Selects discounted products by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType} productType
 */
export const getDiscountedProductsFromProductType = createSelector(
  [selectProducts, (_state: GlobalStateType, productType: ProductType) => productType],
  (products, productType): ProductsEntityState => getDiscountedProductsByType(products, productType)
);

/**
 * Selects the weekly products from the root state by product type.
 *
 * @param  {GlobalStateType} state - top-level state
 * @param  {ProductType | undefined} productType
 */
export const getWeeklyProductsFromProductType = createSelector(
  [selectProducts, (_: GlobalStateType, productType: ProductType | undefined) => productType],
  (products, productType): ProductsEntityState => getWeeklyProductsByType(products, productType)
);
